import { Book, Calendar, Check, Download, Moon, Shield, Sparkle, Unplugged } from '../icons'
import type { LinkPayload, LinkSupplement } from '../link/payload'

const DAY_MS = 86_400_000

function todayEpochDay(): number {
  return Math.floor(Date.now() / DAY_MS)
}

function daysAway(epochDay: number | undefined): number | null {
  if (epochDay === undefined) return null
  return epochDay - todayEpochDay()
}

function plural(count: number, one: string, many: string): string {
  return count === 1 ? one : many
}

/** The app's own wording, repeated rather than reinvented, so the two surfaces agree. */
function purityHeading(payload: LinkPayload): { title: string; note: string; day: number | null } {
  const { purity } = payload
  const historical = purity.framing === 'historical'
  switch (purity.state) {
    case 'niddah':
      return {
        title: historical ? 'You would have been set apart' : 'Set-apart days',
        note: 'Leviticus 15:19, seven days from onset',
        day: purity.dayNumber ?? null,
      }
    case 'prolonged':
      return {
        title: historical ? 'The count would not have started' : 'The count has not started',
        note: 'Leviticus 15:25, the flow is still running',
        day: null,
      }
    case 'clean-count':
      return {
        title: 'Seven clean days',
        note: 'Leviticus 15:28, counted after the flow ended',
        day: purity.dayNumber ?? null,
      }
    case 'complete':
      return { title: 'The days are fulfilled', note: 'Leviticus 15:28', day: 7 }
    default:
      return { title: 'No count running', note: 'Log a period in the app to start one', day: null }
  }
}

function capsuleLabel(gelatin: string): { label: string; clean: boolean } {
  switch (gelatin) {
    case 'NONE':
      return { label: 'No gelatin', clean: true }
    case 'BEEF':
      return { label: 'Bovine gelatin', clean: true }
    case 'FISH':
      return { label: 'Fish gelatin', clean: true }
    case 'HPMC':
      return { label: 'Plant capsule (hypromellose)', clean: true }
    case 'PULLULAN':
      return { label: 'Plant capsule (pullulan)', clean: true }
    case 'CELLULOSE':
      return { label: 'Plant capsule (vegetable cellulose)', clean: true }
    case 'PORK':
      return { label: 'Pork gelatin', clean: false }
    default:
      return { label: 'Capsule not recorded', clean: false }
  }
}

/**
 * The dashboard, once a phone has handed something over.
 *
 * Read-only, deliberately and permanently. Every control that would write is absent rather than
 * disabled: this tab holds a copy of a moment, and a checkbox here that appeared to log a dose
 * would be lying to her about where the truth lives. The app is the only place anything is
 * recorded, and the portal's job is to be a larger screen, not a second source.
 */
export function PrivatePortal({ payload, via }: { payload: LinkPayload; via: 'local' | 'file' }) {
  const today = todayEpochDay()
  const purity = purityHeading(payload)
  const nextPeriod = daysAway(payload.cycle.predictedNextStartEpochDay)
  const inFertileWindow =
    payload.cycle.fertileStartEpochDay !== undefined &&
    payload.cycle.fertileEndEpochDay !== undefined &&
    today >= payload.cycle.fertileStartEpochDay &&
    today <= payload.cycle.fertileEndEpochDay

  const todayRow = payload.cycleDays.find((day) => day.epochDay === today)
  const takenCount = payload.supplements.filter((s) => s.pillsToday > 0).length
  const lowCount = payload.supplements.filter(
    (s) => s.pillsRemaining !== undefined && s.pillsRemaining <= s.lowStockThreshold,
  ).length
  const uncleanCount = payload.supplements.filter((s) => !capsuleLabel(s.gelatin).clean).length
  const nextFeast = payload.biblical?.upcomingFeasts?.[0]

  return (
    <div className="portal-body">
      <section className="overview-grid">
        <article className="console-card overview-card">
          <div className="overview-top">
            <span className="console-label">
              <Moon size={13} /> Biblical day
            </span>
            {payload.biblical ? (
              <span className="vchip vchip-appointed">
                {payload.biblical.isSabbath ? 'Sabbath' : `Day ${payload.biblical.day}`}
              </span>
            ) : null}
          </div>
          <p className="overview-figure">
            {payload.biblical ? `Month ${payload.biblical.month}, day ${payload.biblical.day}` : 'Not sent'}
          </p>
          <div className="overview-foot">
            <span>{payload.biblical?.feastName ? 'Appointed day' : 'Ordinary day'}</span>
            <strong>{payload.biblical?.feastName ?? '—'}</strong>
          </div>
        </article>

        <article className="console-card overview-card">
          <div className="overview-top">
            <span className="console-label">
              <Sparkle size={13} /> Cycle
            </span>
            {inFertileWindow ? <span className="vchip vchip-clean">Fertile window</span> : null}
          </div>
          <p className="overview-figure">
            {nextPeriod === null
              ? 'Not enough logged'
              : nextPeriod > 0
                ? `${nextPeriod} ${plural(nextPeriod, 'day', 'days')}`
                : nextPeriod === 0
                  ? 'Expected today'
                  : 'Date has passed'}
          </p>
          <div className="overview-foot">
            <span>{nextPeriod !== null && nextPeriod > 0 ? 'Until your next period' : 'Next period'}</span>
            <strong>
              {payload.cycle.averageCycleDays ? `${payload.cycle.averageCycleDays} day average` : '—'}
            </strong>
          </div>
        </article>

        <article className="console-card overview-card">
          <div className="overview-top">
            <span className="console-label">
              <Book size={13} /> Leviticus 15
            </span>
            {payload.purity.enabled ? null : <span className="vchip vchip-quiet">Off</span>}
          </div>
          <p className="overview-figure">
            {payload.purity.enabled && purity.day ? `Day ${purity.day} of 7` : purity.title}
          </p>
          <div className="overview-foot">
            <span>{payload.purity.enabled ? purity.note : 'Turn the count on in the app'}</span>
          </div>
        </article>

        <article className="console-card overview-card">
          <div className="overview-top">
            <span className="console-label">
              <Shield size={13} /> Capsule audit
            </span>
            <span className={uncleanCount === 0 ? 'vchip vchip-clean' : 'vchip vchip-refused'}>
              {uncleanCount === 0 ? 'All clear' : `${uncleanCount} to check`}
            </span>
          </div>
          <p className="overview-figure">
            {takenCount} of {payload.supplements.length} taken
          </p>
          <div className="overview-foot">
            <span>Running low</span>
            <strong>{lowCount}</strong>
          </div>
        </article>
      </section>

      {nextFeast ? (
        <section className="feast-banner">
          <Calendar size={20} className="feast-banner-icon" />
          <div>
            <h2 className="console-heading" style={{ fontSize: '21px' }}>
              {nextFeast.name}
            </h2>
            <p className="console-sub">
              {(() => {
                const away = daysAway(nextFeast.epochDay)
                if (away === null) return 'On the published calendar.'
                if (away <= 0) return 'Today.'
                if (away === 1) return 'Tomorrow.'
                const line = `In ${away} days.`
                if (
                  payload.cycle.predictedNextStartEpochDay !== undefined &&
                  Math.abs(nextFeast.epochDay - payload.cycle.predictedNextStartEpochDay) <= 3
                ) {
                  return `${line} Your period is predicted to fall around it, which is worth knowing while there is still time to prepare.`
                }
                return line
              })()}
            </p>
          </div>
        </section>
      ) : null}

      <div className="dash-grid">
        <div className="dash-col">
          {payload.purity.enabled ? (
            <section className="console-card">
              <div className="overview-top">
                <div>
                  <h2 className="console-heading" style={{ fontSize: '22px' }}>
                    {purity.title}
                  </h2>
                  <p className="console-sub">{purity.note}</p>
                </div>
                {purity.day ? <span className="vchip vchip-clean">Day {purity.day} of 7</span> : null}
              </div>
              <div className="pips" style={{ marginTop: 16 }}>
                {[1, 2, 3, 4, 5, 6, 7].map((number) => {
                  const counted = purity.day !== null && number <= purity.day
                  const current = purity.day === number
                  return (
                    <div
                      key={number}
                      className={
                        current ? 'pip pip-current' : counted ? 'pip pip-counted' : 'pip'
                      }
                    >
                      <span className="console-label" style={{ fontSize: 9, color: 'inherit' }}>
                        Day
                      </span>
                      <span className="pip-num">{number}</span>
                      {counted ? <Check size={11} /> : null}
                    </div>
                  )
                })}
              </div>
              <p className="console-sub" style={{ marginTop: 14 }}>
                Counted on your phone, shown here. Nothing on this page changes the count.
              </p>
            </section>
          ) : null}

          <section className="console-card">
            <div className="overview-top">
              <div>
                <h2 className="console-heading" style={{ fontSize: '22px' }}>
                  Today's signs
                </h2>
                <p className="console-sub">The three you read for yourself.</p>
              </div>
              {inFertileWindow ? <span className="vchip vchip-clean">Fertile</span> : null}
            </div>
            <div className="biomarkers" style={{ marginTop: 14 }}>
              <div
                className={
                  todayRow?.cervicalFluid === 'EGG_WHITE' ? 'biomarker biomarker-peak' : 'biomarker'
                }
              >
                <span className="console-label">Cervical fluid</span>
                <p className="biomarker-value">{fluidLabel(todayRow?.cervicalFluid)}</p>
              </div>
              <div className="biomarker">
                <span className="console-label">Waking temp</span>
                <p className="biomarker-value">
                  {todayRow?.basalTempF !== undefined ? `${todayRow.basalTempF.toFixed(1)}°F` : 'Not logged'}
                </p>
              </div>
              <div className="biomarker">
                <span className="console-label">Energy</span>
                <p className="biomarker-value">{energyLabel(todayRow?.energy)}</p>
              </div>
            </div>
          </section>
        </div>

        <div className="dash-col">
          <section className="console-card">
            <div className="overview-top">
              <div>
                <h2 className="console-heading" style={{ fontSize: '22px' }}>
                  Today's stack
                </h2>
                <p className="console-sub">
                  {takenCount} of {payload.supplements.length} taken, and every capsule checked
                  against Leviticus 11.
                </p>
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 14 }}>
              {payload.supplements.map((item) => (
                <StackRow key={item.id} item={item} />
              ))}
              {payload.supplements.length === 0 ? (
                <p className="console-sub">Nothing on the shelf on this phone yet.</p>
              ) : null}
            </div>
          </section>

          <section className="console-card" style={{ display: 'flex', gap: 18, alignItems: 'center' }}>
            <div style={{ flex: 1 }}>
              <h2 className="console-heading" style={{ fontSize: '20px' }}>
                A summary for your midwife
              </h2>
              <p className="console-sub">
                Cycle lengths, luteal phase and the supplements you take, laid out for an
                appointment. Built in this tab from what is already on screen, so it never reaches
                a server on its way to the printer.
              </p>
            </div>
            <button type="button" className="cbtn cbtn-primary" onClick={() => window.print()}>
              <Download size={16} />
              Print or save as PDF
            </button>
          </section>
        </div>
      </div>

      <footer className="pairing-foot">
        <Unplugged size={15} />
        {via === 'local'
          ? `Served by ${payload.deviceLabel ?? 'your phone'} over your own network, built at ${new Date(payload.exportedAtMillis).toLocaleTimeString()}.`
          : `Read from a file your phone wrote at ${new Date(payload.exportedAtMillis).toLocaleString()}.`}{' '}
        Nothing here has been saved.
      </footer>
    </div>
  )
}

function fluidLabel(value: string | undefined): string {
  switch (value) {
    case 'DRY':
      return 'Dry'
    case 'STICKY':
      return 'Sticky'
    case 'CREAMY':
      return 'Creamy'
    case 'EGG_WHITE':
      return 'Egg white'
    default:
      return 'Not logged'
  }
}

function energyLabel(value: string | undefined): string {
  switch (value) {
    case 'LOW':
      return 'Low'
    case 'STEADY':
      return 'Steady'
    case 'HIGH':
      return 'High'
    default:
      return 'Not logged'
  }
}

function StackRow({ item }: { item: LinkSupplement }) {
  const capsule = capsuleLabel(item.gelatin)
  const taken = item.pillsToday > 0
  const low = item.pillsRemaining !== undefined && item.pillsRemaining <= item.lowStockThreshold
  return (
    <div className={taken ? 'stack-row stack-row-taken' : 'stack-row'}>
      <span className={taken ? 'stack-check stack-check-on' : 'stack-check'} aria-hidden="true">
        <Check size={15} />
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <strong style={{ fontSize: 15 }}>{item.name}</strong>
          {low ? (
            <span className="vchip vchip-refused">
              {item.pillsRemaining === 0 ? 'Out' : `${item.pillsRemaining} left`}
            </span>
          ) : null}
        </div>
        <div className="stack-meta">
          {item.brand ? <span>{item.brand}</span> : null}
          {item.brand ? <span className="sep">·</span> : null}
          <span style={{ color: capsule.clean ? 'var(--verified)' : 'var(--refused)' }}>
            {capsule.label}
          </span>
          {item.fastSafe ? <span className="sep">·</span> : null}
          {item.fastSafe ? <span style={{ color: 'var(--primary-text)' }}>Fast-safe</span> : null}
        </div>
      </div>
      <span className={taken ? 'vchip vchip-clean' : 'vchip vchip-quiet'}>
        {taken ? `${item.pillsToday} ${plural(item.pillsToday, 'pill', 'pills')}` : 'Not yet'}
      </span>
    </div>
  )
}
